import * as React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Box, CircularProgress, Stack } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";

import { authGate } from "@/api/connectivity";
import { keys, useMe } from "@/api/hooks";
import { useServerStatusPoller } from "@/api/serverStatus";
import { ReconnectingScreen } from "@/features/auth/ReconnectingScreen";
import { LegalUpdateNotice } from "@/features/legal/LegalUpdateNotice";
import { OrgTermsBlockedBanner } from "@/features/legal/OrgTermsBlockedBanner";
import { OrgTermsGate } from "@/features/legal/OrgTermsGate";
import {
  clearOrgTermsRequired,
  ORG_GATE_EXIT_PATHS,
  useOrgTermsRequired,
} from "@/features/legal/orgTermsGate";
import { OrgTermsPrompt } from "@/features/legal/OrgTermsPrompt";
import { TermsGate } from "@/features/legal/TermsGate";
import { useTermsRequiredFlag } from "@/features/legal/termsGate";
import { loginPath } from "@/lib/nextPath";

const WELCOME_PATH = "/app/welcome";

/** The documents an org admin accepts for the organisation (spec §9.1). */
const ORG_TERMS_DOCS: ReadonlySet<string> = new Set(["terms", "dpa"]);

export function RequireAuth({ children }: { children: React.ReactNode }): React.JSX.Element {
  const me = useMe();
  const location = useLocation();
  const qc = useQueryClient();
  // A write answered 428 (a new version was published while this tab was
  // open): show the gate now and ask /auth/me what is left to accept.
  const termsFlag = useTermsRequiredFlag();
  React.useEffect(() => {
    if (termsFlag) void qc.invalidateQueries({ queryKey: keys.me });
  }, [termsFlag, qc]);
  // A write answered 428 org_terms_acceptance_required (spec §8.4).
  const orgFlag = useOrgTermsRequired();
  // Whether the active org's terms prompt is open, as it reports it.
  const [prompt, setPrompt] = React.useState<{ slug: string; open: boolean } | null>(null);
  React.useEffect(() => {
    if (orgFlag) void qc.invalidateQueries({ queryKey: keys.me });
  }, [orgFlag, qc]);
  // The flag goes once /auth/me, fetched after the 428, reports that org
  // current (another admin accepted), or when the active org changes.
  const flagActiveSlug = me.data?.admin_scope?.active_org_slug ?? null;
  const flagOrgState = me.data?.org_legal?.state;
  const previousActive = React.useRef(flagActiveSlug);
  React.useEffect(() => {
    if (previousActive.current !== flagActiveSlug) {
      previousActive.current = flagActiveSlug;
      clearOrgTermsRequired();
      return;
    }
    if (
      orgFlag &&
      orgFlag.slug === flagActiveSlug &&
      flagOrgState === "current" &&
      me.dataUpdatedAt > orgFlag.at
    ) {
      clearOrgTermsRequired();
    }
  }, [orgFlag, flagActiveSlug, flagOrgState, me.dataUpdatedAt]);
  const promptSlug = me.data?.admin_scope?.active_org_slug ?? null;
  const onPromptOpenChange = React.useCallback(
    (open: boolean) => setPrompt(promptSlug ? { slug: promptSlug, open } : null),
    [promptSlug],
  );
  // Only a 401 signs the user out. A failed /auth/me during a deploy
  // (502/503/504, connection refused) used to bounce a signed-in user to
  // /login; cached data now keeps the app up, and with no data yet the
  // reconnect screen waits for the backend.
  const gate = authGate(me);
  // The one poller of /api/version for every signed-in page: the
  // maintenance banner and the reconnect screen read what it fetches.
  useServerStatusPoller({ reconnecting: gate === "reconnecting" });
  if (gate === "signed-out") {
    // A deep link (a notification e-mail, a "Show me" link) survives
    // sign-in: the login page returns to it.
    const { pathname, search, hash } = location;
    return <Navigate to={loginPath(`${pathname}${search}${hash}`)} replace />;
  }
  if (gate === "reconnecting") return <ReconnectingScreen probe={me} />;
  if (gate === "loading") {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }
  // Terms 2.4: until the current Terms and Privacy Policy are accepted,
  // the gate replaces the app (the server refuses writes meanwhile).
  if ((me.data?.legal_required ?? []).length > 0 || termsFlag) {
    return <TermsGate />;
  }
  // Authenticated but in no organisation (possible under SaaS-mode
  // settings): the org-scoped app shell would render empty lists and
  // 403s, so route to the onboarding flow instead. Global admins are
  // exempt — they see every org without memberships.
  const scope = me.data?.admin_scope;
  const needsOrg =
    !!scope && scope.org_member_slugs.length === 0 && !scope.is_global_admin;
  if (needsOrg && location.pathname !== WELCOME_PATH) {
    return <Navigate to={WELCOME_PATH} replace />;
  }
  // Spec §9.1: the active org's acceptance of the Terms and the DPA.
  // Global admins are exempt (the backend sends no org_legal for them).
  const orgLegal = me.data?.org_legal ?? null;
  const activeSlug = scope?.active_org_slug ?? null;
  const exempt = Boolean(scope?.is_global_admin);
  const adminOfActive = !!activeSlug && (scope?.org_admin_slugs ?? []).includes(activeSlug);
  const activeLapsed =
    orgLegal?.state === "lapsed" || (!!orgFlag && orgFlag.slug === activeSlug);
  const onExitPage = ORG_GATE_EXIT_PATHS.some((p) => location.pathname.startsWith(p));
  if (!exempt && activeSlug && adminOfActive && activeLapsed && !onExitPage) {
    return <OrgTermsGate slug={activeSlug} />;
  }
  // The banner: a lapsed active org (members; admins on the exit pages),
  // or another org a write reached.
  let banner: { slug: string; isAdmin: boolean } | null = null;
  if (!exempt && activeSlug && activeLapsed) {
    banner = { slug: activeSlug, isAdmin: adminOfActive };
  } else if (!exempt && orgFlag) {
    banner = { slug: orgFlag.slug, isAdmin: orgFlag.isAdmin };
  }
  const prompting = !exempt && !!activeSlug && adminOfActive && orgLegal?.state === "pending";
  // The Terms and the DPA an admin accepts for the org: the prompt (and
  // the gate) shows them, and accepting there records them as seen, so
  // the notice leaves them out. Nothing shows while the prompt is open.
  const coveredByOrgTerms = prompting || (!exempt && !!activeSlug && adminOfActive && activeLapsed);
  const notices = (me.data?.legal_notices ?? []).filter(
    (n) => !(coveredByOrgTerms && ORG_TERMS_DOCS.has(n.doc)),
  );
  const promptClosed = prompt?.slug === activeSlug && !prompt.open;
  const shownNotices = prompting && !promptClosed ? [] : notices;
  return (
    <>
      {prompting && activeSlug && (
        <OrgTermsPrompt key={activeSlug} slug={activeSlug} onOpenChange={onPromptOpenChange} />
      )}
      {children}
      {(banner || shownNotices.length > 0) && (
        <LegalStack>
          {banner && <OrgTermsBlockedBanner slug={banner.slug} isAdmin={banner.isAdmin} />}
          <LegalUpdateNotice notices={shownNotices} />
        </LegalStack>
      )}
    </>
  );
}

/** The page's bottom padding grows by this while the legal stack shows,
 *  so it never covers the end of a page (AppShell's main reads it). */
const LEGAL_STACK_HEIGHT_VAR = "--rw-legal-stack-height";
/** In development the Query devtools button sits in the bottom-right
 *  corner; on a phone the stack starts above it. */
const DEV_CORNER_BUTTON = import.meta.env.DEV ? 72 : 0;

/** The fixed bottom stack of legal banners. */
function LegalStack({ children }: { children: React.ReactNode }): React.JSX.Element {
  const ref = React.useRef<HTMLDivElement | null>(null);
  React.useLayoutEffect(() => {
    const el = ref.current;
    const root = document.documentElement;
    if (!el) return undefined;
    const update = () => root.style.setProperty(LEGAL_STACK_HEIGHT_VAR, `${el.offsetHeight + 16}px`);
    update();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update);
    observer?.observe(el);
    return () => {
      observer?.disconnect();
      root.style.removeProperty(LEGAL_STACK_HEIGHT_VAR);
    };
  }, []);
  return (
    <Stack
      ref={ref}
      data-testid="legal-stack"
      sx={{
        position: "fixed",
        zIndex: (theme) => theme.zIndex.snackbar,
        bottom: { xs: 16 + DEV_CORNER_BUTTON, sm: 16 },
        left: 16,
        right: 16,
        gap: 1,
        maxWidth: 640,
        mx: "auto",
      }}
    >
      {children}
    </Stack>
  );
}
